<xemex-card-grid>
  <!-- Location card -->
  <xemex-card
    [title]="'locations.detailTabs.general.label' | translate"
    [actionLabel]="canPlaceOnMap() ? ('locations.coordinates.placeOnMap' | translate) : undefined"
    [actionIcon]="canPlaceOnMap() ? 'edit_location_alt' : undefined"
    (action)="onPlaceOnMap()"
  >
    <div class="grid grid-cols-2 gap-x-6 gap-y-4 mb-4">
      <xemex-detail-field [label]="'locations.table.address' | translate" [loading]="isLoading()">
        {{ locationAddressDetails }}
      </xemex-detail-field>
      <xemex-detail-field [label]="'locations.table.zipcode' | translate" [loading]="isLoading()">
        {{ locationDetail()?.locationPostalCode }}
      </xemex-detail-field>
      <xemex-detail-field [label]="'locations.table.city' | translate" [loading]="isLoading()">
        {{ locationDetail()?.locationCity }}
      </xemex-detail-field>
      <xemex-detail-field [label]="'locations.table.country' | translate" [loading]="isLoading()">
        {{ locationDetail()?.locationCountry }}
      </xemex-detail-field>
    </div>
    <div class="h-48 rounded-lg overflow-hidden border border-gray-200 mb-3">
      @if (locationDetail()?.locationLatitude && locationDetail()?.locationLongitude) {
        <xemex-branches-map
          [latitude]="$safeNavigationMigration(locationDetail()?.locationLatitude)"
          [longitude]="$safeNavigationMigration(locationDetail()?.locationLongitude)"
        ></xemex-branches-map>
      } @else {
        <div class="h-full flex items-center justify-center text-gray-400 text-sm bg-gray-50">
          {{ 'locations.message.noCoordinates' | translate }}
        </div>
      }
    </div>
    @if (canEditLocation()) {
      <div class="flex justify-end">
        <xemex-filled-button icon="edit" (click)="onUpdate()">
          {{ 'general.btnChange' | translate }}
        </xemex-filled-button>
      </div>
    }
  </xemex-card>

  <!-- Partner card -->
  <xemex-card
    [title]="'locations.detailTabs.company.label' | translate"
    [actionLabel]="locationDetail()?.partnerOid ? 'More' : undefined"
    actionIcon="chevron_right"
    [loading]="isLoading()"
    (action)="viewPartner.emit(locationDetail()?.partnerOid ?? '')"
  >
    @if (locationDetail()?.partnerName) {
      <div class="flex flex-col gap-3">
        <xemex-detail-field label="Name">
          {{ locationDetail()?.partnerName }}
        </xemex-detail-field>
      </div>
    } @else {
      <div class="flex justify-center items-center py-8">
        <img src="assets/images/empty/document.svg" alt="" />
      </div>
    }
  </xemex-card>

  <!-- Services card -->
  <xemex-card
    [title]="'locations.detailTabs.services.label' | translate"
    [actionLabel]="(servicesByLocationId()?.numberOfItems ?? 0) > 0 ? 'More' : undefined"
    actionIcon="chevron_right"
    [loading]="isLoadingServices()"
    [subtitle]="servicesSubtitle()"
  >
    @if (!previewServices().length) {
      <div class="flex justify-center items-center py-6">
        <img src="assets/images/empty/document.svg" alt="" />
      </div>
    } @else {
      <div class="flex flex-col divide-y divide-gray-100">
        @for (svc of previewServices(); track svc.serviceId) {
          <div class="flex items-center justify-between py-2.5">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-none">
                <mat-icon class="text-primary !text-sm !flex !items-center !justify-center">
                  credit_card
                </mat-icon>
              </div>
              <div>
                <div class="text-sm font-semibold">
                  {{
                    (svc.package | async)?.packageDescription ||
                      (svc.package | async)?.packageCode ||
                      ''
                  }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ (svc.package | async)?.packageCode }} · {{ svc.term }}
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <xemex-status-chip
                [status]="serviceChipStatus(svc.status)"
                [label]="'services.status.' + svc.status | translate"
              />
              <mat-icon class="text-gray-400 !text-lg">chevron_right</mat-icon>
            </div>
          </div>
        }
      </div>
    }
  </xemex-card>

  <!-- Devices card -->
  <xemex-card
    [title]="'locations.detailTabs.devices.label' | translate"
    [actionLabel]="(devicesByLocationId()?.numberOfItems ?? 0) > 0 ? 'More' : undefined"
    actionIcon="chevron_right"
    [loading]="isLoadingDevices()"
    [subtitle]="devicesSubtitle()"
    (action)="onViewAllDevices()"
  >
    @if (canLinkDevice()) {
      <div class="mb-3">
        <xemex-filled-button type="button" (click)="onLinkDevice()">
          {{ 'locations.linkDevice.btnLink' | translate }}
        </xemex-filled-button>
      </div>
    }
    @if (!previewDevices().length) {
      <div class="flex justify-center items-center py-6">
        <img src="assets/images/empty/document.svg" alt="" />
      </div>
    } @else {
      <div class="flex flex-col divide-y divide-gray-100">
        @for (dev of previewDevices(); track dev.deviceId) {
          <div
            class="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded"
            (click)="viewDevice.emit(dev.deviceId)"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-none"
              >
                <mat-icon class="text-gray-500 !text-sm !flex !items-center !justify-center">
                  router
                </mat-icon>
              </div>
              <div>
                <div class="text-sm font-semibold">{{ dev.deviceLDN }}</div>
                <div class="text-xs text-gray-500">{{ dev.deviceType }}</div>
              </div>
            </div>
            <xemex-status-chip
              [status]="deviceOnline(deviceChipStatus(dev)) ? 'success' : 'inactive'"
              [label]="deviceChipStatus(dev)"
            />
          </div>
        }
      </div>
    }
  </xemex-card>

  <!-- Installations card -->
  @if (showInstallations() && locationDetail(); as location) {
    <xemex-location-installations-card
      class="contents"
      [location]="location"
      [canCreate]="canCreateInstallation()"
      [refresh]="installationsRevision()"
    />
  }

  <!-- Installation groups card -->
  @if (canViewInstallationGroups()) {
    <xemex-card
      [title]="'locations.detailTabs.installationGroups.label' | translate"
      [loading]="isLoading()"
    >
      @if (!installationGroups().length) {
        <div class="flex justify-center items-center py-6">
          <img src="assets/images/empty/document.svg" alt="" />
        </div>
      } @else {
        <div class="flex flex-col divide-y divide-gray-100">
          @for (group of installationGroups(); track group.installationGroupOid) {
            <div
              class="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded"
              (click)="onViewInstallationGroup(group.installationGroupOid)"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-none"
                >
                  <mat-icon class="text-gray-500 !text-sm !flex !items-center !justify-center">
                    workspaces
                  </mat-icon>
                </div>
                <div class="text-sm font-semibold">{{ group.name }}</div>
              </div>
              <mat-icon class="text-gray-400 !text-lg">chevron_right</mat-icon>
            </div>
          }
        </div>
      }
    </xemex-card>
  }
</xemex-card-grid>
